import { TextField } from "@avenue-8/sidekick-ui";
import { NumericFormat } from "react-number-format";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  EditableKeyFeatures,
  getFieldType,
  isNumericFieldValueAllowed,
  validateKeyFeatureField,
} from "../helpers/key-features-form-helpers";
import styled from "@emotion/styled";

type Props = {
  valueName: EditableKeyFeatures;
  value: string;
  onSave: (value: string) => void;
  onCancel: () => void;
  autoFocus?: boolean;
  onError?: (error: string | null) => void;
};

const TinyTextField = styled(TextField)`
  max-width: 120px;
  & .MuiOutlinedInput-root {
    height: 24px;
    & input {
      padding: 0 0.5rem !important;
      text-align: center;
      font-size: ${(p) => p.theme.text.fontSizes.l};
    }
  }
`;

export const KeyFeatureEditableInput = (props: Props) => {
  const {
    valueName,
    value,
    onSave,
    onCancel,
    autoFocus = true,
    onError,
  } = props;
  const [inputValue, setInputValue] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const fieldType = getFieldType(valueName);

  useEffect(() => {
    setInputValue(value);
    setError(null);
  }, [onError, value]);

  useEffect(() => {
    onError?.(error);
  }, [error, onError]);

  const validateAndSave = useCallback(
    async (newValue: any) => {
      setIsValidating(true);

      try {
        const validationError = await validateKeyFeatureField(
          valueName,
          newValue
        );

        if (validationError) {
          setError(validationError);
          setIsValidating(false);
          return;
        }

        setError(null);
        onSave(newValue);
      } catch {
        setError("Invalid value");
      } finally {
        setIsValidating(false);
      }
    },
    [valueName, onSave]
  );

  const handleCancel = useCallback(() => {
    setError(null);
    onCancel();
  }, [onCancel]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && !error && !isValidating) {
        event.preventDefault();
        validateAndSave(inputValue);
      } else if (event.key === "Escape") {
        event.preventDefault();
        handleCancel();
      }
    },
    [inputValue, error, isValidating, validateAndSave, handleCancel]
  );

  const handleBlur = useCallback(() => {
    if (error || isValidating) {
      handleCancel();
      return;
    }

    if (inputValue === value) {
      handleCancel();
      return;
    }

    validateAndSave(inputValue);
  }, [inputValue, value, error, isValidating, validateAndSave, handleCancel]);

  switch (fieldType) {
    case "currency":
      return (
        <NumericFormat
          customInput={TinyTextField}
          value={inputValue}
          thousandSeparator=","
          prefix="$"
          allowNegative={false}
          decimalScale={0}
          onValueChange={(values) => {
            setInputValue(values.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          error={!!error}
          size="small"
          variant="outlined"
          autoFocus={autoFocus}
          hideHelperTextSpace
          getInputRef={inputRef}
          isAllowed={({ floatValue }) =>
            isNumericFieldValueAllowed(valueName, floatValue)
          }
        />
      );

    case "sqft":
      return (
        <NumericFormat
          customInput={TinyTextField}
          value={inputValue}
          thousandSeparator=","
          allowNegative={false}
          decimalScale={0}
          suffix=" sq ft"
          onValueChange={(values) => {
            setInputValue(values.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          error={!!error}
          helperText={error || ""}
          size="small"
          variant="outlined"
          autoFocus={autoFocus}
          hideHelperTextSpace
          getInputRef={inputRef}
          isAllowed={({ floatValue }) =>
            isNumericFieldValueAllowed(valueName, floatValue)
          }
        />
      );

    case "number":
      return (
        <NumericFormat
          customInput={TinyTextField}
          value={inputValue}
          allowNegative={false}
          decimalScale={0}
          onValueChange={(values) => {
            setInputValue(values.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          error={!!error}
          helperText={error || ""}
          size="small"
          variant="outlined"
          autoFocus={autoFocus}
          hideHelperTextSpace
          getInputRef={inputRef}
          isAllowed={({ floatValue }) =>
            isNumericFieldValueAllowed(valueName, floatValue)
          }
        />
      );

    case "boolean":
      return (
        <TinyTextField
          select
          value={inputValue?.toLowerCase()}
          SelectProps={{
            native: true,
          }}
          onChange={(event) => {
            const newValue = event.target.value;
            setInputValue(newValue);
            setError(null);
            validateAndSave(newValue);
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          error={!!error}
          helperText={error || ""}
          size="small"
          variant="outlined"
          inputRef={inputRef}
          autoFocus={autoFocus}
          hideHelperTextSpace
        >
          {/* <option value="">-</option> */}
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </TinyTextField>
      );

    case "text":
    default:
      return (
        <TinyTextField
          value={inputValue}
          onChange={(event) => {
            setInputValue(event.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          error={!!error}
          helperText={error || ""}
          size="small"
          variant="outlined"
          inputRef={inputRef}
          autoFocus={autoFocus}
          hideHelperTextSpace
        />
      );
  }
};
